/*
 * Copyright (c) 2024-2025 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../.../../../logger';
import { SiteConfig } from '../config';
import { Placement } from './index';
import { PrebidVideoPlacement } from './video/prebid';

import type { CampaignData } from '../../types/index';
import type { PrebidAdUnit } from '../../types/prebid';

const logger = Logger.getInstance();

class PlacementManager {
  private static instance: PlacementManager;
  private placementFactory: (
    adUnit: PrebidAdUnit,
    adUnitTransformer: (adUnitCode: string) => string,
    campaignData: CampaignData[],
    siteConfig: SiteConfig
  ) => Placement | undefined;
  private placementCache: Map<string, Placement>;

  private constructor(
    placementFactory: (
      adUnit: PrebidAdUnit,
      adUnitTransformer: (adUnitCode: string) => string,
      campaignData: CampaignData[],
      siteConfig: SiteConfig
    ) => Placement | undefined
  ) {
    this.placementFactory = placementFactory;
    this.placementCache = new Map<string, Placement>();
  }

  public static getInstance(
    placementFactory: (
      adUnit: PrebidAdUnit,
      adUnitTransformer: (adUnitCode: string) => string,
      campaignData: CampaignData[],
      siteConfig: SiteConfig
    ) => Placement | undefined
  ): PlacementManager {
    if (!PlacementManager.instance) {
      PlacementManager.instance = new PlacementManager(placementFactory);
    }
    return PlacementManager.instance;
  }

  public getPlacement(
    adUnit: PrebidAdUnit,
    campaignData: CampaignData[] = [],
    adUnitTransformer: (adUnitCode: string) => string = (adUnitCode) => adUnitCode,
    siteConfig: SiteConfig
  ): Placement | undefined {
    // Check cache first
    const adUnitCode = adUnitTransformer(adUnit.code);
    const cachedPlacement = this.placementCache.get(adUnitCode);
    if (cachedPlacement) {
      logger.info(`Reusing existing placement for adUnit: ${adUnitCode}`);
      return cachedPlacement;
    }

    logger.info(`Creating new placement for adUnit: ${adUnitCode}`);
    let placement: Placement | undefined;

    // Check if mediaTypes only has video property
    const mediaTypes = adUnit.mediaTypes;
    const hasVideo = !!mediaTypes.video;
    const hasBanner = !!mediaTypes.banner;
    const hasOtherMediaTypes =
      Object.keys(mediaTypes).some((key) => key !== 'video' && key !== 'banner') || hasBanner;

    if (hasVideo && !hasOtherMediaTypes) {
      // Only video mediaType present, return VideoPlacement
      placement = new PrebidVideoPlacement(adUnit, adUnitTransformer, campaignData, siteConfig);
    } else {
      // Use the factory for all other cases
      try {
        placement = this.placementFactory(adUnit, adUnitTransformer, campaignData, siteConfig);
      } catch (error) {
        logger.error(`Error creating placement for adUnit: ${adUnitCode}: ${error}`);
        return;
      }
    }

    // Cache and return
    this.placementCache.set(adUnitCode, placement!);
    return placement;
  }
}

export { PlacementManager };
